import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../style/ServicesPublic.css';
import { useModal } from '../context/ModalContext';
import cyberImg from '../images/capability-cyber.jpg';
import cloudImg from '../images/capability-cloud.jpg';
import enterpriseImg from '../images/capability-enterprise.jpg';
import servicenowImg from '../images/capability-servicenow.jpg';
import aiImg from '../images/capability-ai.jpg';

const SERVICES = [
  {
    id: 1,
    serviceCode: 'SVC_CYBER',
    name: 'Cyber Security',
    shortDescription: 'Secure Your Business End-to-End',
    details: [
      'Zero Trust Architecture',
      'Identity & Access Management (IAM)',
      'Risk & Compliance (SOC2, ISO, etc.)',
      'Threat Detection & Response',
      'Cloud Security',
    ],
    tagline: 'Security embedded across your entire ecosystem.',
    icon: 'security',
    image: cyberImg,
  },
  {
    id: 2,
    serviceCode: 'SVC_CLOUD',
    name: 'Cloud Services',
    shortDescription: 'Build Scalable & Resilient Platforms',
    details: [
      'Cloud Migration (AWS, Azure, GCP)',
      'Architecture & Design',
      'DevOps & Automation',
      'Cost Optimization',
      'Managed Cloud Services',
    ],
    tagline: 'Flexible, scalable, and high-performing environments.',
    icon: 'cloud',
    image: cloudImg,
  },
  {
    id: 3,
    serviceCode: 'SVC_ENTERPRISE',
    name: 'Enterprise Applications',
    shortDescription: 'SAP & Oracle ERP Solutions',
    details: [
      'SAP Implementation & Support',
      'Oracle ERP Solutions',
      'Integration & Data Migration',
      'Application Modernization',
      'Managed ERP Services',
    ],
    tagline: 'Transforming core enterprise systems.',
    icon: 'business_center',
    image: enterpriseImg,
  },
  {
    id: 4,
    serviceCode: 'SVC_SERVICENOW',
    name: 'ServiceNow',
    shortDescription: 'Automate & Streamline Operations',
    details: [
      'ITSM Implementation',
      'HR Service Delivery (HRSD)',
      'SecOps & GRC',
      'Workflow Automation',
      'Platform Integration',
    ],
    tagline: 'Driving operational efficiency and automation.',
    icon: 'autorenew',
    image: servicenowImg,
  },
  {
    id: 5,
    serviceCode: 'SVC_AI',
    name: 'AI & Data',
    shortDescription: 'Turn Data into Business Value',
    details: [
      'Data Engineering',
      'AI / Machine Learning',
      'Predictive Analytics',
      'Data Visualization',
      'Intelligent Automation',
    ],
    tagline: 'Helping organizations make smarter decisions.',
    icon: 'psychology',
    image: aiImg,
  },
];

const ServicesPublic = () => {
  const { isModalOpen, openModal, closeModal, selectedService } = useModal();
  const navigate = useNavigate();
  const { serviceCode } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);

    // If serviceCode is in URL, open the modal for that service
    if (serviceCode) {
      const matchingService = SERVICES.find(s => s.serviceCode === serviceCode);
      if (matchingService) {
        openModal(matchingService);
      }
    }

    document.title = 'Services — KovanTech Solutions';
  }, [serviceCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLearnMore = (service) => openModal(service);
  const handleCloseModal = () => closeModal();

  return (
    <div className="services-page">
      {/* ══ Gradient Hero Section ══ */}
      <section className="services-hero">
        <div className="services-hero-content">
          <span className="hero-label">SERVICES & EXPERTISE</span>
          <h1 className="services-hero-title">
            Delivering secure, scalable, and intelligent solutions<br />
            across <span className="hero-highlight">enterprise technologies.</span>
          </h1>
          <p className="services-hero-desc">
            We deliver enterprise solutions across Cyber Security, Cloud, SAP, ServiceNow, and AI to help organizations scale securely and efficiently.
          </p>
        </div>
      </section>

      {/* ══ Services Grid ══ */}
      <section className="services-grid-section">
        <div className="services-grid-container">
          {SERVICES.map((service) => (
            <div key={service.id} className="service-card-public">
              <div className="card-image-box">
                <img src={service.image} alt={service.name} />
              </div>
              <h3 className="card-title">{service.name}</h3>
              <p className="card-desc">{service.shortDescription}</p>
              <ul className="card-bullets">
                {service.details.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <button className="card-learn-more" onClick={() => handleLearnMore(service)}>
                <span>Learn More</span>
                <span className="material-symbols-outlined arrow-icon">arrow_forward</span>
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ══ Our Methodology ══ */}
      <section className="methodology-section">
        <div className="methodology-container">
          <div className="methodology-header">
            <h2>Our Methodology</h2>
            <p>A structured approach to solving complex technical challenges.</p>
          </div>
          <div className="methodology-steps">
            <div className="method-line"></div>
            <div className="method-step">
              <div className="method-circle">
                <span className="material-symbols-outlined">search</span>
              </div>
              <h4>Discover</h4>
              <p>Understand business goals, challenges, and technology landscape.</p>
            </div>
            <div className="method-step">
              <div className="method-circle">
                <span className="material-symbols-outlined">architecture</span>
              </div>
              <h4>Design</h4>
              <p>Architect scalable, secure, and enterprise-ready solutions.</p>
            </div>
            <div className="method-step">
              <div className="method-circle">
                <span className="material-symbols-outlined">rocket_launch</span>
              </div>
              <h4>Implement</h4>
              <p>Deliver SAP, Cloud, ServiceNow, and Cyber solutions with precision.</p>
            </div>
            <div className="method-step">
              <div className="method-circle">
                <span className="material-symbols-outlined">settings</span>
              </div>
              <h4>Optimize</h4>
              <p>Continuous monitoring, security, and performance optimization.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ CTA Section ══ */}
      <section className="services-cta-section">
        <div className="services-cta-box">
          <div className="cta-glow-orb top-right"></div>
          <div className="cta-glow-orb bottom-left"></div>
          <div className="services-cta-text">
            <h2>Ready to transform your tech stack?</h2>
            <p>Partner with Kovan and build the future of your organization today.</p>
          </div>
          <div className="services-cta-actions">
            <a href="mailto:hr@kovantech.com" className="btn btn-outline-white">Schedule a Call</a>
          </div>
        </div>
      </section>

      {/* ══ Modal ══ */}
      {isModalOpen && selectedService && (
        <div className="service-modal-overlay" onClick={handleCloseModal}>
          <div className="service-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-icon-box">
                  <span className="material-symbols-outlined">{selectedService.icon || 'business_center'}</span>
                </div>
                <div>
                  <h2>{selectedService.name}</h2>
                  <span className="modal-subtitle">Service Details</span>
                </div>
              </div>
              <button onClick={handleCloseModal} className="modal-close">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3>Overview</h3>
                <p>{selectedService.shortDescription}</p>
                {selectedService.details && selectedService.details.length > 0 && (
                  <ul className="modal-details-list">
                    {selectedService.details.map((item, i) => (
                      <li key={i}>
                        <span className="material-symbols-outlined modal-bullet-icon">check_circle</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {selectedService.tagline && (
                  <p className="modal-tagline">{selectedService.tagline}</p>
                )}
              </div>

              <div className="modal-section">
                <h3>What We Offer</h3>
                <div className="offer-grid">
                  <div className="offer-card">
                    <span className="material-symbols-outlined offer-icon">check_circle</span>
                    <strong>Expert Team</strong>
                    <p>Dedicated specialists with years of experience in {selectedService.name.toLowerCase()}.</p>
                  </div>
                  <div className="offer-card">
                    <span className="material-symbols-outlined offer-icon">schedule</span>
                    <strong>Fast Delivery</strong>
                    <p>Quick turnaround times without compromising on quality.</p>
                  </div>
                  <div className="offer-card">
                    <span className="material-symbols-outlined offer-icon">security</span>
                    <strong>Quality Assurance</strong>
                    <p>Rigorous testing and validation at every stage of development.</p>
                  </div>
                  <div className="offer-card">
                    <span className="material-symbols-outlined offer-icon">support_agent</span>
                    <strong>Ongoing Support</strong>
                    <p>Continuous maintenance and support after project completion.</p>
                  </div>
                </div>
              </div>

            </div>

            <div className="modal-actions">
              <button onClick={handleCloseModal} className="btn-modal-close">Close</button>
              <button
                onClick={() => {
                  closeModal();
                  navigate('/contact');
                }}
                className="btn-modal-get-started"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPublic;
